// Global Paddle.js loader — initialize once per SPA lifetime.
// We use a promise cache so multiple checkout buttons on the same page (BuildYourFile,
// ApplyReview) share the same Paddle instance without racing on init.
import { initializePaddle } from "@paddle/paddle-js";

let _paddlePromise = null;

/**
 * Returns a memoised promise that resolves to a ready Paddle.js instance,
 * or `null` if env vars are missing / initialisation failed.
 *
 * Uses `REACT_APP_PADDLE_ENV` ("sandbox" | "production") and
 * `REACT_APP_PADDLE_CLIENT_TOKEN` (starts with `test_` for sandbox).
 */
export function getPaddle() {
  if (_paddlePromise) return _paddlePromise;
  const env = process.env.REACT_APP_PADDLE_ENV || "sandbox";
  const token = process.env.REACT_APP_PADDLE_CLIENT_TOKEN;
  if (!token) {
    console.warn("Paddle: REACT_APP_PADDLE_CLIENT_TOKEN missing");
    return Promise.resolve(null);
  }
  _paddlePromise = initializePaddle({
    environment: env,
    token,
  }).then((p) => p || null).catch((e) => {
    console.error("Paddle init failed:", e);
    _paddlePromise = null; // allow retry on next click
    return null;
  });
  return _paddlePromise;
}

/**
 * Open the Paddle Billing overlay against a server-created transaction_id.
 * Resolves once the overlay opens (or rejects on init failure). The webhook
 * handles order-status flip; frontend polls `/payments/status/{txn_id}` on
 * the success page.
 */
export async function openPaddleCheckout({ transactionId, successUrl, customerEmail }) {
  const paddle = await getPaddle();
  if (!paddle) throw new Error("Paddle SDK unavailable — please refresh and try again.");
  paddle.Checkout.open({
    transactionId,
    ...(customerEmail ? { customer: { email: customerEmail } } : {}),
    settings: {
      theme: "light",
      displayMode: "overlay",
      allowLogout: false,
      showAddDiscounts: true,
      // Force a normalised locale — some browsers / test harnesses report
      // exotic tags (e.g. "en-US@posix") that break Paddle's Intl formatter.
      locale: "en",
      ...(successUrl ? { successUrl } : {}),
    },
  });
}
